// src/pages/Tasks.tsx
//
// Mentee-facing Tasks tab.
//
// ─────────────────────────────────────────────────────────────────
// DATA SOURCES
// ─────────────────────────────────────────────────────────────────
//   useGetMyQuarterQuery     — personal quarter status for Q1-Q4 selector
//   useGetTasksQuery         — task definitions + merged submission state
//   useStartTaskMutation     — POST /api/tasks/:id/start  (idempotent)
//   useSaveDraftMutation     — PATCH /api/tasks/:id/draft (high-frequency auto-save)
//   useSubmitTaskMutation    — POST /api/tasks/:id/submit (final submission)
//
// ─────────────────────────────────────────────────────────────────
// TASK LIFECYCLE (matches backend TASK_STATUS)
// ─────────────────────────────────────────────────────────────────
//   not_started  — card shows "Start Task" button, no submission exists yet
//   in_progress  — inline editor open; draft auto-saved every 3 s
//   submitted    — read-only view of submitted text; awaiting feedback
//   graded       — feedback card with ScoreRing shown
//
// ─────────────────────────────────────────────────────────────────
// EDITOR RULES (enforced client-side before server validation)
// ─────────────────────────────────────────────────────────────────
//   • Word count is computed live from the textarea value
//   • Progress percent = min(99, Math.round((wordCount / minWords) * 100))
//     so 100% is reserved for the submitted state
//   • Auto-save fires 3 s after the last keystroke (debounced)
//   • Submit is disabled until wordCount >= minWords
//   • A confirmation dialog prevents accidental final submission
//   • Attachment URL / name can optionally be added before submitting
//
// ─────────────────────────────────────────────────────────────────
// QUARTER LOCKING (personal, mirrors useGetMyQuarterQuery.quarterSummary)
// ─────────────────────────────────────────────────────────────────
//   completed  — read-only; all tasks and feedback visible
//   active     — full access; editor, draft, submit all available
//   upcoming   — teaser state; task titles shown, no editor or submission
//   locked     — completely hidden (mentee hasn't reached this quarter)

import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import {
  ClipboardList,
  CheckCircle2,
  Clock,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Lock,
  Star,
  MessageSquare,
  Upload,
  Calendar,
  BarChart2,
  Filter,
  Flame,
  Loader2,
  Check,
  X,
  FileText,
  Paperclip,
  RefreshCw,
  TrendingUp,
  Zap,
} from "lucide-react";
import {
  TaskStatus,
  TaskWithSubmission,
  useGetTasksQuery,
  useSaveDraftMutation,
  useStartTaskMutation,
  useSubmitTaskMutation,
} from "../../app/api/tasksApi";
import {
  QuarterKey,
  QuarterSummaryItem,
  useGetMyQuarterQuery,
} from "../../app/api/programmeApi";

/* ─────────────────────────────────────────────────────────────────
   CONSTANTS
───────────────────────────────────────────────────────────────── */

const AUTO_SAVE_DELAY_MS = 3000; // 3 s debounce on draft saves
const URGENT_DAYS = 7; // tasks due within this many days show warning

type FilterTab = "all" | "pending" | "completed";

/* ─────────────────────────────────────────────────────────────────
   DISPLAY CONSTANTS
───────────────────────────────────────────────────────────────── */

const STATUS_META: Record<
  TaskStatus,
  { label: string; colour: string; bg: string; icon: React.ReactNode }
> = {
  not_started: {
    label: "Not started",
    colour: "text-gray-500",
    bg: "bg-gray-100",
    icon: <Clock size={12} />,
  },
  in_progress: {
    label: "In progress",
    colour: "text-amber-700",
    bg: "bg-amber-100",
    icon: <BarChart2 size={12} />,
  },
  submitted: {
    label: "Submitted",
    colour: "text-blue-700",
    bg: "bg-blue-100",
    icon: <Upload size={12} />,
  },
  graded: {
    label: "Graded",
    colour: "text-green-700",
    bg: "bg-green-100",
    icon: <CheckCircle2 size={12} />,
  },
};

const isCompleted = (s?: TaskStatus) => s === "submitted" || s === "graded";

const progressColour = (p: number) =>
  p === 0
    ? "bg-gray-200"
    : p < 40
      ? "bg-red-400"
      : p < 70
        ? "bg-amber-400"
        : "bg-green-500";

/* ─────────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────────── */

const countWords = (s: string) => s.trim().split(/\s+/).filter(Boolean).length;

const fmtDate = (d?: string | null) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "—";

/* ─────────────────────────────────────────────────────────────────
   SKELETON
───────────────────────────────────────────────────────────────── */

const Bone: React.FC<{ className?: string }> = ({ className = "" }) => (
  <div className={`animate-pulse rounded-xl bg-gray-100 ${className}`} />
);

/* ─────────────────────────────────────────────────────────────────
   STATUS CHIP
───────────────────────────────────────────────────────────────── */

const StatusChip: React.FC<{ status: TaskStatus }> = ({ status }) => {
  const m = STATUS_META[status];
  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${m.bg} ${m.colour}`}
    >
      {m.icon}
      {m.label}
    </span>
  );
};

/* ─────────────────────────────────────────────────────────────────
   PROGRESS BAR
───────────────────────────────────────────────────────────────── */

const ProgressBar: React.FC<{ percent: number }> = ({ percent }) => (
  <div className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
    <div
      className={`h-full rounded-full transition-all duration-500 ${progressColour(percent)}`}
      style={{ width: `${percent}%` }}
    />
  </div>
);

/* ─────────────────────────────────────────────────────────────────
   SCORE RING  (SVG donut used in the feedback panel)
───────────────────────────────────────────────────────────────── */

const ScoreRing: React.FC<{ score: number }> = ({ score }) => {
  const pct = (score / 10) * 100;
  const r = 18;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  const colour = score >= 8 ? "#16a34a" : score >= 5 ? "#d97706" : "#dc2626";
  return (
    <div className="relative flex items-center justify-center w-14 h-14 shrink-0">
      <svg width="56" height="56" className="-rotate-90">
        <circle
          cx="28"
          cy="28"
          r={r}
          fill="none"
          stroke="#f3f4f6"
          strokeWidth="4"
        />
        <circle
          cx="28"
          cy="28"
          r={r}
          fill="none"
          stroke={colour}
          strokeWidth="4"
          strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round"
        />
      </svg>
      <span className="absolute text-sm font-bold" style={{ color: colour }}>
        {score}
      </span>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   SUBMIT CONFIRM MODAL
───────────────────────────────────────────────────────────────── */

const SubmitConfirmModal: React.FC<{
  task: TaskWithSubmission;
  wordCount: number;
  isLoading: boolean;
  apiError: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ task, wordCount, isLoading, apiError, onConfirm, onCancel }) => {
  const meetsMin = wordCount >= task.minWords;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center shrink-0">
            <Upload size={18} className="text-green-600" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900">
              Submit Task {task.taskNumber}?
            </h3>
            <p className="text-xs text-gray-500">
              This cannot be undone. Make sure your response is complete.
            </p>
          </div>
        </div>

        {/* Readiness checklist */}
        <div className="space-y-2 mb-4">
          {[
            {
              ok: meetsMin,
              label: `Word count: ${wordCount} / ${task.minWords} minimum`,
            },
            {
              ok: !!task.description,
              label: "Task brief understood",
            },
          ].map((c, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              <span
                className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 ${c.ok ? "bg-green-500" : "bg-red-100"}`}
              >
                {c.ok ? (
                  <Check size={9} className="text-white" />
                ) : (
                  <X size={8} className="text-red-400" />
                )}
              </span>
              <span
                className={c.ok ? "text-green-700 font-medium" : "text-red-500"}
              >
                {c.label}
              </span>
            </div>
          ))}
        </div>

        {!meetsMin && (
          <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mb-4">
            <AlertCircle size={12} className="shrink-0" />
            You need at least {task.minWords - wordCount} more word
            {task.minWords - wordCount !== 1 ? "s" : ""} before you can submit.
          </div>
        )}

        {apiError && (
          <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2 mb-4">
            <AlertCircle size={12} className="shrink-0" />
            {apiError}
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 py-2 text-sm font-medium border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Keep editing
          </button>
          <button
            disabled={!meetsMin || isLoading}
            onClick={onConfirm}
            className="flex-1 py-2 text-sm font-semibold bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-1.5">
                <Loader2 size={13} className="animate-spin" />
                Submitting…
              </span>
            ) : (
              "Submit now"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   TASK EDITOR
   Shown when a task is in_progress. Manages local draft state,
   debounced auto-save to the backend, word counting, and final
   submission flow.
───────────────────────────────────────────────────────────────── */

interface EditorProps {
  task: TaskWithSubmission;
  onClose: () => void;
  onSubmitted: () => void;
}

const TaskEditor: React.FC<EditorProps> = ({ task, onClose, onSubmitted }) => {
  const sub = task.submission;

  // Initialise from existing draft content
  const [content, setContent] = useState(sub.draftContent ?? "");
  const [attachmentUrl, setAttachmentUrl] = useState(sub.attachmentUrl ?? "");
  const [attachmentName, setAttachmentName] = useState(
    sub.attachmentName ?? "",
  );
  const [showAttach, setShowAttach] = useState(!!sub.attachmentUrl);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<
    "idle" | "saving" | "saved" | "error"
  >("idle");

  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [saveDraft] = useSaveDraftMutation();
  const [submitTask, { isLoading: isSubmitting }] = useSubmitTaskMutation();

  const wc = useMemo(() => countWords(content), [content]);
  const pct = Math.min(99, Math.round((wc / task.minWords) * 100));
  const canSubmit = wc >= task.minWords;

  // Debounced auto-save
  const triggerAutoSave = useCallback(
    (newContent: string) => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
      autoSaveTimer.current = setTimeout(async () => {
        setSaveState("saving");
        try {
          await saveDraft({
            taskId: task.id,
            draftContent: newContent,
            progressPercent: Math.min(
              99,
              Math.round((countWords(newContent) / task.minWords) * 100),
            ),
            wordCount: countWords(newContent),
          }).unwrap();
          setSaveState("saved");
          setTimeout(() => setSaveState("idle"), 2500);
        } catch {
          setSaveState("error");
        }
      }, AUTO_SAVE_DELAY_MS);
    },
    [saveDraft, task.id, task.minWords],
  );

  const handleContentChange = (val: string) => {
    setContent(val);
    triggerAutoSave(val);
  };

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    };
  }, []);

  const handleSubmit = async () => {
    setSubmitError(null);
    try {
      await submitTask({
        taskId: task.id,
        draftContent: content,
        wordCount: wc,
        attachmentUrl: attachmentUrl || undefined,
        attachmentName: attachmentName || undefined,
      }).unwrap();
      setShowConfirm(false);
      onSubmitted();
    } catch (err: any) {
      setSubmitError(
        err?.data?.error ?? "Submission failed. Please try again.",
      );
    }
  };

  return (
    <>
      <div className="border-t border-gray-50 bg-gray-50/50 px-5 py-5 space-y-4">
        {/* Editor header */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-gray-700 uppercase tracking-widest">
              Your response
            </p>
            <p className="text-[11px] text-gray-400 mt-0.5">
              Minimum {task.minWords} words ·{" "}
              {task.dueDate
                ? `Due ${fmtDate(task.dueDate)}`
                : "No deadline set"}
            </p>
          </div>
          {/* Auto-save indicator */}
          <div className="flex items-center gap-1.5 text-[11px] shrink-0">
            {saveState === "saving" && (
              <span className="flex items-center gap-1 text-gray-400">
                <Loader2 size={11} className="animate-spin" /> Saving…
              </span>
            )}
            {saveState === "saved" && (
              <span className="flex items-center gap-1 text-green-600">
                <Check size={11} /> Saved
              </span>
            )}
            {saveState === "error" && (
              <span className="flex items-center gap-1 text-red-500">
                <AlertCircle size={11} /> Save failed
              </span>
            )}
          </div>
        </div>

        {/* Textarea */}
        <div className="relative">
          <textarea
            rows={12}
            value={content}
            onChange={(e) => handleContentChange(e.target.value)}
            placeholder={`Write your response here…\n\nMinimum ${task.minWords} words required.`}
            className="w-full text-sm text-gray-800 bg-white rounded-xl border border-gray-200 px-4 py-3 resize-none
              focus:outline-none focus:ring-2 focus:ring-green-300 focus:border-transparent
              placeholder-gray-300 leading-relaxed transition"
          />
          {/* Live word count badge */}
          <div
            className={`absolute bottom-3 right-3 text-[10px] font-bold px-2 py-0.5 rounded-full transition-colors
            ${wc >= task.minWords ? "bg-green-100 text-green-700" : wc > 0 ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-400"}`}
          >
            {wc} / {task.minWords} words
          </div>
        </div>

        {/* Writing progress bar */}
        <div className="space-y-1">
          <ProgressBar percent={pct} />
          <p className="text-[11px] text-gray-400">
            {wc === 0
              ? "Start writing to see your progress"
              : wc < task.minWords
                ? `${task.minWords - wc} more word${task.minWords - wc !== 1 ? "s" : ""} needed`
                : `✓ Word count met — ready to submit`}
          </p>
        </div>

        {/* Optional attachment */}
        <div>
          <button
            onClick={() => setShowAttach((v) => !v)}
            className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-green-700 transition-colors"
          >
            <Paperclip size={12} />
            {showAttach ? "Remove attachment" : "Add attachment (optional)"}
          </button>
          {showAttach && (
            <div className="mt-2 grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[11px] font-semibold text-gray-600 mb-1">
                  Attachment URL
                </label>
                <input
                  type="url"
                  placeholder="https://…"
                  value={attachmentUrl}
                  onChange={(e) => setAttachmentUrl(e.target.value)}
                  className="w-full text-xs px-3 py-2 rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-green-300 placeholder-gray-300 transition"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-gray-600 mb-1">
                  File name
                </label>
                <input
                  type="text"
                  placeholder="e.g. governance-audit.pdf"
                  value={attachmentName}
                  onChange={(e) => setAttachmentName(e.target.value)}
                  className="w-full text-xs px-3 py-2 rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-green-300 placeholder-gray-300 transition"
                />
              </div>
            </div>
          )}
        </div>

        {/* Action row */}
        <div className="flex items-center gap-3 pt-1 border-t border-gray-100">
          <button
            onClick={() => setShowConfirm(true)}
            disabled={!canSubmit || isSubmitting}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all shadow-sm
              ${
                canSubmit
                  ? "bg-green-600 text-white hover:bg-green-700 active:scale-95"
                  : "bg-gray-100 text-gray-400 cursor-not-allowed"
              }`}
          >
            <Upload size={14} />
            Submit Task
          </button>
          <button
            onClick={onClose}
            className="text-xs font-medium text-gray-400 hover:text-gray-600 transition-colors"
          >
            Close editor
          </button>
          {!canSubmit && wc > 0 && (
            <span className="text-[11px] text-amber-600 flex items-center gap-1">
              <AlertCircle size={10} /> {task.minWords - wc} more word
              {task.minWords - wc !== 1 ? "s" : ""} needed
            </span>
          )}
        </div>
      </div>

      {/* Submission confirm modal */}
      {showConfirm && (
        <SubmitConfirmModal
          task={task}
          wordCount={wc}
          isLoading={isSubmitting}
          apiError={submitError}
          onConfirm={handleSubmit}
          onCancel={() => {
            setShowConfirm(false);
            setSubmitError(null);
          }}
        />
      )}
    </>
  );
};

/* ─────────────────────────────────────────────────────────────────
   TASK CARD
   Full lifecycle card: expand to see instructions / editor / feedback.
───────────────────────────────────────────────────────────────── */

interface TaskCardProps {
  task: TaskWithSubmission;
  isLocked: boolean;
  isExpanded: boolean;
  activeQuarterStatus: "completed" | "active" | "upcoming" | "locked";
  onToggle: () => void;
  onEditorClosed: () => void;
}

const TaskCard: React.FC<TaskCardProps> = ({
  task,
  isLocked,
  isExpanded,
  activeQuarterStatus,
  onToggle,
  onEditorClosed,
}) => {
  const sub = task.submission;
  const status = sub.status;
  const done = isCompleted(status);
  const isActive = activeQuarterStatus === "active";

  const daysRem = task.daysRemaining;
  const isOverdue = daysRem !== null && daysRem < 0 && !done;
  const isUrgent =
    daysRem !== null && daysRem >= 0 && daysRem <= URGENT_DAYS && !done;

  const [showEditor, setShowEditor] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [startTask] = useStartTaskMutation();

  const handleStart = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setStartError(null);
    setIsStarting(true);
    try {
      await startTask(task.id).unwrap();
      setShowEditor(true);
      if (!isExpanded) onToggle();
    } catch (err: any) {
      setStartError(
        err?.data?.error ?? "Could not start task. Please try again.",
      );
    } finally {
      setIsStarting(false);
    }
  };

  const handleContinue = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowEditor(true);
    if (!isExpanded) onToggle();
  };

  return (
    <div
      className={`bg-white rounded-2xl border shadow-sm transition-all duration-200 overflow-hidden
        ${
          isLocked
            ? "opacity-50 border-gray-100"
            : done
              ? "border-green-100"
              : isOverdue
                ? "border-red-200"
                : isUrgent
                  ? "border-amber-200"
                  : "border-gray-100 hover:border-gray-200"
        }`}
    >
      {/* Late / overdue ribbon */}
      {sub.isLate && sub.status !== "not_started" && (
        <div className="bg-red-500 text-white text-[10px] font-bold px-4 py-1 text-center tracking-wide uppercase">
          Submitted late
        </div>
      )}
      {isOverdue && !done && (
        <div className="bg-red-100 border-b border-red-200 text-red-700 text-[10px] font-semibold px-5 py-1.5 flex items-center gap-1.5">
          <AlertCircle size={11} /> Overdue — submit as soon as possible
        </div>
      )}

      {/* Card header — always visible, clickable to expand */}
      <button
        onClick={isLocked ? undefined : onToggle}
        disabled={isLocked}
        className="w-full text-left px-5 py-4 flex items-start gap-4 group"
      >
        {/* Task number circle */}
        <div
          className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold mt-0.5
          ${
            done
              ? "bg-green-100 text-green-700"
              : isOverdue
                ? "bg-red-100 text-red-600"
                : isUrgent
                  ? "bg-amber-100 text-amber-700"
                  : "bg-gray-100 text-gray-600"
          }`}
        >
          {done ? <CheckCircle2 size={16} /> : task.taskNumber}
        </div>

        <div className="flex-1 min-w-0">
          {/* Title + status */}
          <div className="flex items-start justify-between gap-2 mb-1">
            <h3
              className={`text-sm font-semibold leading-snug
              ${
                isLocked
                  ? "text-gray-400"
                  : done
                    ? "text-green-800"
                    : "text-gray-900 group-hover:text-green-700 transition-colors"
              }`}
            >
              Task {task.taskNumber} — {task.title}
            </h3>
            <div className="flex items-center gap-2 shrink-0">
              {isLocked && <Lock size={13} className="text-gray-400" />}
              {!isLocked && <StatusChip status={status} />}
              {sub.isLate && isCompleted(status) && (
                <span className="text-[10px] font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded-full">
                  Late
                </span>
              )}
            </div>
          </div>

          {/* Description */}
          {task.description && (
            <p className="text-xs text-gray-500 leading-relaxed line-clamp-2 mb-2">
              {task.description}
            </p>
          )}

          {/* Meta row */}
          <div className="flex items-center gap-3 text-[11px] text-gray-400 flex-wrap">
            {task.dueDate && (
              <span className="flex items-center gap-1">
                <Calendar size={11} /> Due {fmtDate(task.dueDate)}
              </span>
            )}
            {daysRem !== null && !done && daysRem >= 0 && (
              <span
                className={`flex items-center gap-1 font-semibold ${isUrgent ? "text-amber-600" : "text-gray-500"}`}
              >
                {isUrgent && <AlertCircle size={11} />}
                {daysRem === 0 ? "Due today" : `${daysRem}d remaining`}
              </span>
            )}
            {isOverdue && !done && (
              <span className="flex items-center gap-1 font-semibold text-red-500">
                <AlertCircle size={11} /> {Math.abs(daysRem!)}d overdue
              </span>
            )}
            {sub.submittedAt && (
              <span className="flex items-center gap-1 text-green-600">
                <Upload size={11} /> Submitted {fmtDate(sub.submittedAt)}
              </span>
            )}
            <span className="text-gray-300">·</span>
            <span>Min. {task.minWords} words</span>
            {sub.wordCount !== undefined && sub.wordCount > 0 && (
              <span className="text-gray-400">{sub.wordCount} written</span>
            )}
          </div>

          {/* Progress bar — only for in_progress */}
          {status === "in_progress" && !isLocked && (
            <div className="mt-3 space-y-1">
              <ProgressBar percent={sub.progressPercent} />
              <p className="text-[11px] text-gray-400">
                {sub.progressPercent === 0
                  ? "Not started yet"
                  : `${sub.progressPercent}% complete`}
              </p>
            </div>
          )}

          {/* Tags */}
          {task.tags?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {task.tags.slice(0, 4).map((tag) => (
                <span
                  key={tag}
                  className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Quick actions on card header (visible without expanding) */}
          {!isLocked && isActive && (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              {status === "not_started" && (
                <button
                  onClick={handleStart}
                  disabled={isStarting}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-green-600 text-white hover:bg-green-700 active:scale-95 transition-all disabled:opacity-50"
                >
                  {isStarting ? (
                    <Loader2 size={11} className="animate-spin" />
                  ) : (
                    <Zap size={11} />
                  )}
                  Start Task
                </button>
              )}
              {status === "in_progress" && (
                <button
                  onClick={handleContinue}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-amber-500 text-white hover:bg-amber-600 active:scale-95 transition-all"
                >
                  <Upload size={11} /> Continue writing
                </button>
              )}
              {startError && (
                <span className="text-[11px] text-red-500 flex items-center gap-1">
                  <AlertCircle size={10} />
                  {startError}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Expand chevron */}
        {!isLocked && (
          <div className="shrink-0 mt-1 text-gray-400 group-hover:text-green-600 transition-colors">
            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>
        )}
      </button>

      {/* ── EXPANDED PANEL ── */}
      {isExpanded && !isLocked && (
        <div className="border-t border-gray-50 space-y-5">
          {/* Instructions */}
          {task.instructions.length > 0 && (
            <div className="px-5 pt-5">
              <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3">
                Instructions
              </p>
              <ol className="space-y-2">
                {task.instructions.map((step, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 text-sm text-gray-700"
                  >
                    <span className="shrink-0 w-5 h-5 rounded-full bg-green-100 text-green-700 text-[11px] font-bold flex items-center justify-center mt-0.5">
                      {/* instructions can be { stepNumber, instruction } or plain string */}
                      {(step as any).stepNumber ?? i + 1}
                    </span>
                    {(step as any).instruction ?? String(step)}
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Submitted view — shows the submitted text read-only */}
          {(status === "submitted" || status === "graded") &&
            sub.draftContent && (
              <div className="px-5">
                <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">
                  Your submission
                </p>
                <div className="bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 max-h-48 overflow-y-auto">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {sub.draftContent}
                  </p>
                </div>
                <p className="text-[11px] text-gray-400 mt-1.5 flex items-center gap-1">
                  <FileText size={10} />
                  {sub.wordCount ?? 0} words submitted
                  {sub.attachmentName && (
                    <>
                      {" · "}
                      <Paperclip size={9} />
                      <a
                        href={sub.attachmentUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-green-600 hover:underline"
                      >
                        {sub.attachmentName}
                      </a>
                    </>
                  )}
                </p>
              </div>
            )}

          {/* Awaiting feedback notice */}
          {status === "submitted" && !sub.feedback && (
            <div className="px-5">
              <div className="flex items-center gap-3 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
                <Clock size={15} className="text-blue-500 shrink-0" />
                <div>
                  <p className="text-xs font-semibold text-blue-800">
                    Awaiting feedback from your Regional Rep
                  </p>
                  <p className="text-[11px] text-blue-600 mt-0.5">
                    You'll be notified once your rep has reviewed and scored
                    your submission.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Feedback block */}
          {sub.feedback && (
            <div className="px-5">
              <div className="rounded-xl border border-green-100 bg-green-50 p-4 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs font-semibold uppercase tracking-widest text-green-700">
                    Feedback received
                  </p>
                  <ScoreRing score={sub.feedback.score} />
                </div>
                <div className="flex items-start gap-2">
                  <MessageSquare
                    size={13}
                    className="text-green-500 mt-0.5 shrink-0"
                  />
                  <p className="text-sm text-green-900 leading-relaxed">
                    "{sub.feedback.comment}"
                  </p>
                </div>
                <div className="flex items-center justify-between text-[11px] text-green-600">
                  <span className="font-medium">
                    {typeof sub.feedback.gradedBy === "object" &&
                    sub.feedback.gradedBy
                      ? `${(sub.feedback.gradedBy as any).firstName} ${(sub.feedback.gradedBy as any).lastName}`
                      : "Regional Representative"}
                  </span>
                  <span>{fmtDate(sub.feedback.feedbackDate)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Inline editor — shown when in_progress and user clicked write */}
          {status === "in_progress" && showEditor && (
            <TaskEditor
              task={task}
              onClose={() => {
                setShowEditor(false);
                onEditorClosed();
              }}
              onSubmitted={() => {
                setShowEditor(false);
                onEditorClosed();
              }}
            />
          )}

          {/* Open editor button if in_progress but editor not yet shown */}
          {status === "in_progress" && !showEditor && isActive && (
            <div className="px-5 pb-5">
              <button
                onClick={() => setShowEditor(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 active:scale-95 transition-all shadow-sm"
              >
                <Upload size={14} /> Continue & Submit
              </button>
              {sub.draftContent && (
                <p className="text-[11px] text-gray-400 mt-2 flex items-center gap-1">
                  <Check size={10} className="text-green-500" />
                  Draft saved · {sub.wordCount ?? 0} words
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   QUARTER SELECTOR CARD
───────────────────────────────────────────────────────────────── */

const QuarterCard: React.FC<{
  summary: QuarterSummaryItem;
  isSelected: boolean;
  taskStats: { total: number; done: number; urgent: number };
  onClick: () => void;
}> = ({ summary, isSelected, taskStats, onClick }) => {
  const status = summary.status;

  const borderCls =
    status === "locked"
      ? "border-gray-100 bg-gray-50 cursor-not-allowed opacity-60"
      : isSelected && status === "active"
        ? "border-amber-400 bg-amber-50"
        : isSelected && status === "completed"
          ? "border-green-400 bg-green-50"
          : isSelected
            ? "border-gray-400 bg-gray-50"
            : status === "active"
              ? "border-gray-200 hover:border-amber-300 bg-white"
              : status === "completed"
                ? "border-gray-200 hover:border-green-300 bg-white"
                : "border-gray-200 hover:border-gray-300 bg-white";

  return (
    <button
      onClick={onClick}
      disabled={status === "locked"}
      className={`relative text-left rounded-xl border p-4 transition-all duration-150 ${borderCls}`}
    >
      <div className="flex items-start justify-between gap-1 mb-2">
        <span
          className={`text-lg font-bold ${isSelected ? "text-gray-900" : "text-gray-700"}`}
        >
          {summary.quarter}
        </span>
        {/* Status badge */}
        {status === "completed" && (
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">
            {taskStats.done}/{taskStats.total}
          </span>
        )}
        {status === "active" && taskStats.urgent > 0 && (
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 flex items-center gap-0.5">
            <Flame size={9} /> {taskStats.urgent} due
          </span>
        )}
        {status === "active" && taskStats.urgent === 0 && (
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">
            Active
          </span>
        )}
        {status === "upcoming" && (
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
            Soon
          </span>
        )}
        {status === "locked" && <Lock size={12} className="text-gray-400" />}
      </div>
      <p className="text-xs text-gray-500 leading-snug">
        {summary.theme ?? "Theme TBD"}
      </p>

      {/* Mini progress bar for active/completed */}
      {(status === "active" || status === "completed") &&
        taskStats.total > 0 && (
          <div className="mt-2">
            <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${status === "completed" ? "bg-green-500" : "bg-amber-400"}`}
                style={{
                  width: `${Math.round((taskStats.done / taskStats.total) * 100)}%`,
                }}
              />
            </div>
          </div>
        )}
    </button>
  );
};

/* ─────────────────────────────────────────────────────────────────
   MAIN PAGE
───────────────────────────────────────────────────────────────── */

const Tasks: React.FC = () => {
  // ── Quarter context ───────────────────────────────────────────
  const { data: myQRes, isLoading: qLoading } = useGetMyQuarterQuery();
  const myQData = myQRes?.data;

  const [activeQuarter, setActiveQuarter] = useState<QuarterKey | null>(null);
  useEffect(() => {
    if (myQData && !activeQuarter) setActiveQuarter(myQData.currentQuarter);
  }, [myQData, activeQuarter]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const quarterSummaries: QuarterSummaryItem[] = myQData?.quarterSummary ?? [];

  const currentQ = activeQuarter ?? myQData?.currentQuarter ?? "Q1";
  const activeSummary = quarterSummaries.find((q) => q.quarter === currentQ);
  const qStatus = activeSummary?.status ?? "locked";
  const isLocked = qStatus === "locked";
  const isUpcoming = qStatus === "upcoming";

  // ── Tasks for selected quarter ────────────────────────────────
  const {
    data: tasksRes,
    isLoading: tasksLoading,
    isFetching: tasksFetching,
    refetch,
  } = useGetTasksQuery({ quarter: currentQ }, { skip: isLocked });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const allTasks = tasksRes?.data ?? [];

  // ── UI state ──────────────────────────────────────────────────
  const [filterTab, setFilterTab] = useState<FilterTab>("all");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Reset to first active task when quarter changes
  useEffect(() => {
    if (!tasksLoading && allTasks.length > 0) {
      const firstActive = allTasks.find(
        (t) => !isCompleted(t.submission.status),
      );
      setExpandedId(firstActive?.id ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQ, tasksLoading]);

  // ── Filtering ─────────────────────────────────────────────────
  const filtered = useMemo(() => {
    if (filterTab === "pending")
      return allTasks.filter((t) => !isCompleted(t.submission.status));
    if (filterTab === "completed")
      return allTasks.filter((t) => isCompleted(t.submission.status));
    return allTasks;
  }, [allTasks, filterTab]);

  // ── Summary stats ─────────────────────────────────────────────
  const stats = useMemo(() => {
    const total = allTasks.length;
    const done = allTasks.filter((t) =>
      isCompleted(t.submission.status),
    ).length;
    const graded = allTasks.filter(
      (t) => t.submission.status === "graded",
    ).length;
    const urgent = allTasks.filter(
      (t) =>
        !isCompleted(t.submission.status) &&
        t.daysRemaining !== null &&
        t.daysRemaining >= 0 &&
        t.daysRemaining <= URGENT_DAYS,
    ).length;
    const overdue = allTasks.filter(
      (t) =>
        !isCompleted(t.submission.status) &&
        t.daysRemaining !== null &&
        t.daysRemaining < 0,
    ).length;
    const scores = allTasks
      .filter(
        (t) =>
          t.submission.status === "graded" &&
          t.submission.feedback?.score != null,
      )
      .map((t) => t.submission.feedback!.score);
    const avgScore =
      scores.length > 0
        ? scores.reduce((a, b) => a + b, 0) / scores.length
        : null;
    return { total, done, graded, urgent, overdue, avgScore };
  }, [allTasks]);

  // Per-quarter task stats for the quarter selector cards
  const quarterTaskStats = useMemo(() => {
    const m: Record<string, { total: number; done: number; urgent: number }> =
      {};
    // Only current quarter has live data; others we set to zeros
    quarterSummaries.forEach((qs) => {
      m[qs.quarter] = { total: 0, done: 0, urgent: 0 };
    });
    if (currentQ) {
      m[currentQ] = {
        total: stats.total,
        done: stats.done,
        urgent: stats.urgent,
      };
    }
    return m;
  }, [quarterSummaries, currentQ, stats]);

  const toggle = (id: string) =>
    setExpandedId((prev) => (prev === id ? null : id));

  const handleQuarterChange = (q: QuarterKey) => {
    setActiveQuarter(q);
    setFilterTab("all");
    setExpandedId(null);
  };

  /* ──────────────────────────────────────────────────────────── */
  return (
    <div className="space-y-7 pb-12">
      {/* PAGE HEADER */}
      <div>
        <p className="text-xs font-medium uppercase tracking-widest text-green-600 mb-1">
          Leadership Tasks
        </p>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
          Tasks
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Quarterly assignments from the Overseer — practical exercises for
          personal growth and ministry development.
        </p>
      </div>

      {/* QUARTER SELECTOR */}
      {qLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[0, 1, 2, 3].map((i) => (
            <Bone key={i} className="h-24" />
          ))}
        </div>
      ) : (
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3">
            Select quarter
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {quarterSummaries.map((qs) => (
              <QuarterCard
                key={qs.quarter}
                summary={qs}
                isSelected={currentQ === qs.quarter}
                taskStats={
                  quarterTaskStats[qs.quarter] ?? {
                    total: 0,
                    done: 0,
                    urgent: 0,
                  }
                }
                onClick={() => handleQuarterChange(qs.quarter)}
              />
            ))}
          </div>
        </div>
      )}

      {/* STATS BAR — active or completed quarters with data */}
      {!tasksLoading &&
        allTasks.length > 0 &&
        (qStatus === "active" || qStatus === "completed") && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              {
                label: "Tasks done",
                value: `${stats.done}/${stats.total}`,
                sub:
                  stats.total - stats.done > 0
                    ? `${stats.total - stats.done} remaining`
                    : "All complete",
                colour: "text-green-700",
              },
              {
                label: qStatus === "active" ? "Urgent" : "Overdue submitted",
                value: String(
                  qStatus === "active" ? stats.urgent : stats.overdue,
                ),
                sub:
                  qStatus === "active" && stats.urgent > 0
                    ? "Due within 7 days"
                    : qStatus === "active"
                      ? "All on track"
                      : stats.overdue > 0
                        ? "Were submitted late"
                        : "None",
                colour:
                  (qStatus === "active" ? stats.urgent : stats.overdue) > 0
                    ? "text-amber-600"
                    : "text-gray-500",
              },
              {
                label: "Graded",
                value: String(stats.graded),
                sub: "With written feedback",
                colour: "text-blue-600",
              },
              {
                label: "Avg. score",
                value:
                  stats.avgScore !== null
                    ? `${stats.avgScore.toFixed(1)}/10`
                    : "—",
                sub:
                  stats.avgScore !== null
                    ? "Across graded tasks"
                    : "No grades yet",
                colour:
                  stats.avgScore !== null
                    ? stats.avgScore >= 8
                      ? "text-green-700"
                      : stats.avgScore >= 5
                        ? "text-amber-600"
                        : "text-red-600"
                    : "text-gray-400",
              },
            ].map((s) => (
              <div
                key={s.label}
                className="bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-4"
              >
                <p className="text-xs text-gray-400 mb-1">{s.label}</p>
                <p className={`text-xl font-bold ${s.colour}`}>{s.value}</p>
                <p className="text-[11px] text-gray-400 mt-0.5 leading-tight">
                  {s.sub}
                </p>
              </div>
            ))}
          </div>
        )}

      {/* LOCKED */}
      {isLocked && (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
            <Lock size={24} className="text-gray-400" />
          </div>
          <h3 className="text-base font-semibold text-gray-700 mb-1">
            {currentQ} tasks are not yet available
          </h3>
          <p className="text-sm text-gray-400 max-w-sm">
            Tasks for this quarter will be released once you reach {currentQ} in
            your programme journey. Stay active and complete your current
            quarter first.
          </p>
        </div>
      )}

      {/* UPCOMING */}
      {isUpcoming && !isLocked && (
        <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-8 flex flex-col items-center text-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-white border border-gray-200 flex items-center justify-center shadow-sm">
            <ClipboardList size={20} className="text-gray-400" />
          </div>
          <h3 className="text-sm font-semibold text-gray-700">
            {currentQ} tasks will be released at the event
          </h3>
          <p className="text-xs text-gray-400 max-w-sm leading-relaxed">
            Task briefs are distributed within 24 hours of the quarterly event.
            Complete your current quarter's tasks — your performance influences
            Legacy Fellows eligibility.
          </p>
          {activeSummary?.eventDate && (
            <p className="text-xs font-semibold text-green-600 flex items-center gap-1">
              <Zap size={12} /> Event: {fmtDate(activeSummary.eventDate)}
            </p>
          )}
        </div>
      )}

      {/* TASK LIST */}
      {!isLocked && !isUpcoming && (
        <>
          {/* Filter bar */}
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-1 bg-gray-100 rounded-xl p-1">
              {(
                [
                  { key: "all", label: "All", count: allTasks.length },
                  {
                    key: "pending",
                    label: "Pending",
                    count: allTasks.filter(
                      (t) => !isCompleted(t.submission.status),
                    ).length,
                  },
                  {
                    key: "completed",
                    label: "Completed",
                    count: allTasks.filter((t) =>
                      isCompleted(t.submission.status),
                    ).length,
                  },
                ] as { key: FilterTab; label: string; count: number }[]
              ).map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setFilterTab(tab.key)}
                  className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-all duration-150
                    ${filterTab === tab.key ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                >
                  {tab.label}
                  <span
                    className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full
                    ${filterTab === tab.key ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-500"}`}
                  >
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              {tasksFetching && !tasksLoading && (
                <RefreshCw size={12} className="text-gray-400 animate-spin" />
              )}
              <div className="flex items-center gap-1.5 text-xs text-gray-400">
                <Filter size={12} />
                <span>
                  {filtered.length} task{filtered.length !== 1 ? "s" : ""} shown
                </span>
              </div>
            </div>
          </div>

          {/* Loading state */}
          {tasksLoading ? (
            <div className="space-y-3">
              {[0, 1, 2].map((i) => (
                <Bone key={i} className="h-28" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-sm text-gray-400">
              No tasks match this filter.
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  isLocked={false}
                  isExpanded={expandedId === task.id}
                  activeQuarterStatus={qStatus as any}
                  onToggle={() => toggle(task.id)}
                  onEditorClosed={refetch}
                />
              ))}
            </div>
          )}

          {/* All tasks complete banner */}
          {!tasksLoading &&
            qStatus === "active" &&
            stats.done === stats.total &&
            stats.total > 0 && (
              <div className="flex items-center gap-3 rounded-2xl bg-green-50 border border-green-100 px-5 py-4">
                <Star size={20} className="text-green-500 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-green-800">
                    All tasks submitted — well done!
                  </p>
                  <p className="text-xs text-green-600 mt-0.5">
                    Your consistent engagement strengthens your Legacy Fellows
                    eligibility. Watch for feedback from your Regional
                    Representative.
                  </p>
                </div>
              </div>
            )}

          {/* Graded summary banner */}
          {!tasksLoading &&
            qStatus === "completed" &&
            stats.avgScore !== null && (
              <div className="flex items-center gap-4 rounded-2xl bg-purple-50 border border-purple-100 px-5 py-4">
                <TrendingUp size={20} className="text-purple-500 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-purple-800">
                    {currentQ} complete — avg. score {stats.avgScore.toFixed(1)}
                    /10
                  </p>
                  <p className="text-xs text-purple-600 mt-0.5">
                    Your results for this quarter are final. They contribute to
                    your overall programme score.
                  </p>
                </div>
              </div>
            )}
        </>
      )}

      {/* LEGACY FELLOWS NUDGE — active quarter, tasks not all done */}
      {!isLocked &&
        !isUpcoming &&
        qStatus === "active" &&
        stats.done < stats.total &&
        !tasksLoading && (
          <div className="relative overflow-hidden rounded-2xl border border-purple-100 bg-gradient-to-br from-purple-50 to-white p-5 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-purple-100 shrink-0">
              <Flame size={20} className="text-purple-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-gray-900 mb-0.5">
                Task completion affects Legacy Fellows eligibility
              </h3>
              <p className="text-xs text-gray-500 leading-relaxed">
                Submitting all tasks on time is one of the key signals used in
                selecting the 10 Legacy Fellows each quarter. Don't leave
                submissions to the last minute.
              </p>
            </div>
          </div>
        )}
    </div>
  );
};

export default Tasks;
